import { Icon } from '@components/Icon';
import { useHasPermissionFromContext } from '@components/PermissionsContext';
import { getInnerText } from '@components/Table/util/getInnerText';
import { INPUT_HEIGHT } from '@components/Theme/constants';
import { Tooltip } from '@components/Tooltip';
import { CSSObject } from '@emotion/styled';
import { useDebouncedFn } from '@hooks/useDebouncedFn';
import { useFullstoryElement } from '@hooks/useFullstory';
import { useRecentSelections } from '@hooks/useRecentSelections';
import { useTheme } from '@hooks/useTheme';
import { t } from '@lingui/core/macro';
import {
  FS_MASK,
  FullStoryElementType,
  FullStoryElements,
  FullStoryType,
  FullstoryEventTypes,
  fsEvent,
} from '@utils/fullstory';
import { useWorkflow } from '@utils/fullstory/context';
import { throwIfInternalTestOrBelow } from '@utils/invariant';
import { win } from '@utils/win';
import { AUTOCOMPLETE } from '@utils/zIndex';
import Downshift, {
  ControllerStateAndHelpers,
  DownshiftState,
  GetItemPropsOptions,
  StateChangeOptions,
} from 'downshift';
import { isFunction, isString, noop, omit, toLower, trim } from 'lodash-es';
import {
  HTMLProps,
  KeyboardEvent,
  LiHTMLAttributes,
  MutableRefObject,
  PropsWithChildren,
  ReactElement,
  ReactNode,
  forwardRef,
  useEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { usePopper } from 'react-popper';
import { Writable } from 'type-fest';
import { ReadOnlyField } from '../Field/ReadOnlyField';
import { Label } from '../FieldLabel';
import { AUTOCOMPLETE_OFF_VALUE, Input } from '../Input';
import { useMaxItemContainerWidth } from './useMaxItemContainerWidth';
import { getPopperContainer } from './util';

export interface Shell<ItemType> {
  label: string | ((item?: Maybe<fixMe>) => ReactNode);
  value: ItemType | undefined;
  /**  Used for React key attr, falls back to label if no id provided. */
  id?: string | number;
  hidden?: boolean;
  /** Render the item, but disallow selection. */
  disabled?: boolean;
  newLabel?: string;
  hideCheckbox?: boolean;
  /** Only used for title attr. Use label for item rendering. Defaults to label if not specified. */
  titleAttr?: string;
}

type ItemToString<ItemType> = (item: Shell<ItemType> | null) => string;

// ts-unused-exports:disable-next-line
export interface RenderItemContext<ItemType> {
  /** The item being rendered */
  item: Shell<ItemType>;
  /** The item's value */
  value: ItemType | undefined;
  /** Whether this item is currently highlighted (keyboard nav/hover) */
  isHighlighted: boolean;
  /** Whether this item is disabled */
  disabled?: boolean;
  /** The item's index in the list */
  index: number;
  /** Whether this item is currently selected */
  isSelected: boolean;
}

export type RenderItemHelpers<ItemType> = ControllerStateAndHelpers<
  Shell<ItemType>
> &
  FullStoryType & {
    defaultItemStyles: CSSObject;
    key: string;
    item: Shell<ItemType>;
    isHighlighted: boolean;
    index: number;
    itemProps: Record<string, unknown>;
    getItemProps: (options: GetItemPropsOptions<Shell<ItemType>>) => unknown;
    disabled?: boolean;
    newLabel?: string;
    hideCheckbox?: boolean;
    fsItemElement?: FullStoryElementType;
  };

interface ShowAddNewItemShell<ItemType> extends Omit<Shell<ItemType>, 'label'> {
  /** Internal implementation prop, consumers do not need to use this. */
  addNew?: true;
  prefix?: string;
  label?: string;
}

export interface Props<ItemType> extends FullStoryType {
  name?: string;
  disabled?: boolean;
  readOnly?: boolean;
  id?: string;
  initialSelectedItem?: Shell<ItemType>;
  onKeyDown?: (event: KeyboardEvent<HTMLDivElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLDivElement>) => void;
  inputProps?: Omit<
    HTMLProps<HTMLInputElement> & { 'data-testid'?: string },
    'disabled'
  >;
  items: ReadonlyArray<Shell<ItemType>>;
  itemToString?: ItemToString<ItemType>;
  label?: ReactNode;
  loading?: boolean | string;
  onScrollToBottom?: () => void;
  /** Defaults to true. Typically this is what you want as you will not have any results yet. */
  showLoadingMessageOnFirstLoading?: boolean;
  /** This is the event you want to tap into to do asynchronous work. You must provide your own debounced function if you want to do that. Use the useDebouncedFn hook. */
  onInputValueChange?: (
    inputValue: string,
    stateAndHelpers: ControllerStateAndHelpers<Shell<ItemType>>,
  ) => void;
  onChange: (
    item: Shell<ItemType> | null,
    helpers?: ControllerStateAndHelpers<Shell<ItemType>>,
  ) => void;
  openMenuOnFocus?: boolean;
  /** @deprecated Use the `label` callback function method on individual items to customize display. See AutoComplete story code for example. */
  DEPRECATEDrenderItem?: (helpers: RenderItemHelpers<ItemType>) => ReactNode;
  /** Render custom content for each item. The component handles wrapping, styling, and accessibility automatically. */
  renderItem?: (context: RenderItemContext<ItemType>) => ReactNode;
  /** Defaults to false */
  showSearchIcon?: boolean;
  selectedItem?: Maybe<Shell<ItemType>>;
  maxItemSelectLimit?: number;
  selectedItemCount?: number;
  filterOnLabel?: boolean;
  disableSelectOnTab?: boolean;
  initialIsOpen?: boolean;
  isOpen?: boolean;
  downshiftStateReducer?: (
    state: DownshiftState<Shell<ItemType>>,
    changes: StateChangeOptions<Shell<ItemType>>,
  ) => Partial<StateChangeOptions<Shell<ItemType>>>;
  /** Restricts item container to width of parent input, incurring further possibility of word-break */
  restrictItemContainerWidth?: boolean;
  renderDropdownInPopper?: boolean;
  /** The item selection area will appear above the button */
  dropup?: boolean;
  popperZIndex?: number;
  tabIndex?: number;
  triggerInputChangeOnEmpty?: boolean;
  ref?: MutableRefObject<HTMLButtonElement> | null;
  showAddNewItem?: (kwargs: {
    listItems: ReadonlyArray<Shell<ItemType>>;
    inputValue?: Maybe<string>;
  }) => void | ShowAddNewItemShell<unknown>;
  showAddNewItemAtTop?: boolean;
  /** An implementation prop to always force a specific input value from the consuming component. Used mostly by MultiSelectAutoComplete and not needed for normal implementations. */
  controlledInputValue?: boolean;
  defaultHighlightedIndex?: number;
  fsItemElement?: FullStoryElementType;
  fsSearchElement?: FullStoryElementType;
  /** Enable recent selections feature. Requires renderItem prop to be provided. */
  enableRecentSelections?: boolean;
  /** Load and save recent selections for counterfactual analytics without displaying them. */
  enableRecentSelectionAnalytics?: boolean;
  /** Add consumer-specific, privacy-safe properties to the recent-selection event. */
  getRecentSelectionAnalyticsProperties?: (
    item: Shell<ItemType>,
  ) => Record<string, boolean | number | string | undefined>;
  /** Unique key to identify this autocomplete's recent selections in IndexedDB. Required when enableRecentSelections is true. */
  recentSelectionsKey?: string;
  /** Maximum number of recent selections to store and display. Defaults to 5. */
  maxRecentSelections?: number;
  /** Optional callback to refresh recent selections data when the component mounts. Return null to exclude deleted items. */
  refreshRecentSelections?: (
    storedSelections: Array<{
      id: string | number;
      label: string;
      value: anyOk;
      timestamp: number;
    }>,
  ) => Promise<Array<Shell<ItemType> | null | undefined>>;
}

export const getStringFromLabelFunc = (
  item: string | Maybe<Partial<Shell<unknown>>>,
): string => {
  if (isString(item)) {
    return item;
  }
  if (isFunction(item?.label)) {
    let candidate: ReactNode = '';
    if (item && item.label) {
      candidate = item.label(item.value);
    }
    if (isString(candidate)) {
      return candidate;
    } else {
      // TODO this function is EXPENSIVE. It is best to use a string label if at all possible. This is a fallback for a react node label which takes time to compute.
      return getInnerText((<>{candidate}</>) as fixMe);
    }
  }
  return item?.label || '';
};

export function getStringFromItem<ItemType>(
  item: Maybe<Shell<ItemType>>,
  itemToString?: ItemToString<ItemType>,
): string {
  if (item) {
    if (itemToString) {
      return itemToString(item);
    } else if (isString(item.label)) {
      return item.label;
    } else if (isFunction(item.label)) {
      const label: anyOk = item.label();
      return isString(label)
        ? label
        : label?.props?.children && isString(label?.props?.children)
          ? label?.props?.children
          : '';
    } else {
      return '';
    }
  }
  return '';
}

/** Characters to ignore when matching the input value to the list of results */
const charsToIgnoreRegex = /(,|\.|-|'|")/gi;

export function checkFilterMatch<ItemType>(
  item: Shell<ItemType>,
  inputValue: string,
  itemToString?: ItemToString<ItemType>,
): boolean {
  const coercedLabel = toLower(getStringFromItem(item, itemToString))
    .trim()
    .replace(charsToIgnoreRegex, '');

  const coercedInput = toLower(inputValue)
    .trim()
    .replace(charsToIgnoreRegex, '');
  return coercedLabel.includes(coercedInput);
}

const ITEM_MIN_HEIGHT = 32;

// ts-unused-exports:disable-next-line
export const itemStyles: CSSObject = {
  margin: 0,
  padding: '8px 8px',
  minHeight: ITEM_MIN_HEIGHT,
};

export const ITEM_CONTAINER_MAX_HEIGHT = 250;
export const ITEM_CONTAINER_VERT_MARGIN = 2;
export const SELECTALL_KEY = 'selectall';
export const SELECTNONE_KEY = 'selectnone';

interface ItemContainerProps {
  maxWidth?: number | string;
  minWidth?: number | string;
  disableClickBubble?: boolean;
  onScrollToBottom?: () => void;
}

const ItemContainer = forwardRef<
  HTMLUListElement,
  PropsWithChildren<ItemContainerProps>
>(
  (
    {
      children,
      maxWidth,
      minWidth,
      disableClickBubble,
      onScrollToBottom,
      ...rest
    },
    ref,
  ) => {
    const { card, gray, contextMenu } = useTheme();
    return (
      <ul
        css={{
          background: contextMenu.background,
          border: `1px solid ${gray[95]}`,
          borderRadius: 2,
          boxShadow: card.boxShadow,
          listStyleType: 'none',
          margin: 0,
          marginTop: ITEM_CONTAINER_VERT_MARGIN,
          maxHeight: ITEM_CONTAINER_MAX_HEIGHT,
          maxWidth: maxWidth || '100%',
          minWidth: minWidth || '100%',
          overflow: 'auto',
          overscrollBehaviorY: 'none',
          padding: 0,
          position: 'absolute',
          top: '100%',
          width: 'max-content',
          zIndex: AUTOCOMPLETE,
          scrollbarColor: 'auto',
        }}
        onClick={
          disableClickBubble ? (e): void => e.stopPropagation() : undefined
        }
        {...rest}
        onScroll={(event): void => {
          const { clientHeight, scrollHeight, scrollTop } = event.currentTarget;
          if (scrollHeight - scrollTop - clientHeight <= 1) {
            onScrollToBottom?.();
          }
        }}
        ref={ref}
      >
        {children}
      </ul>
    );
  },
);

export const Item = forwardRef<
  anyOk,
  {
    isHighlighted: boolean;
    titleAttr: string;
    disabled?: boolean;
  } & FullStoryType &
    Omit<LiHTMLAttributes<HTMLLIElement>, 'title'>
>(
  (
    {
      children,
      titleAttr: itemLabel,
      isHighlighted,
      disabled,
      fsElement,
      fsType,
      fsParent,
      fsName,
      ...rest
    },
    ref,
  ) => {
    const {
      colors: { primary, text, disabledText },
      card,
    } = useTheme();
    const { getFsComponentProps } = useFullstoryElement();

    return (
      <li
        data-disabled={Boolean(disabled).toString()}
        title={itemLabel}
        css={{
          ...itemStyles,
          cursor: disabled ? 'default' : 'pointer',
          userSelect: 'none',
          backgroundColor: isHighlighted ? primary : 'inherit',
          color: isHighlighted
            ? card.background
            : disabled
              ? disabledText
              : text,
        }}
        {...rest}
        onClick={
          disabled
            ? (e): void => {
                if (disabled) {
                  e.preventDefault();
                }
              }
            : rest.onClick
        }
        ref={ref}
        {...getFsComponentProps({
          name: fsName,
          parent: fsParent,
          element: fsElement,
          type: fsType,
          status: disabled ? 'inActive' : 'active',
        })}
      >
        {children}
      </li>
    );
  },
);

// this is an example of a TS generic react component
export const AutoComplete = <ItemType extends unknown>(
  props: Props<ItemType>,
): ReactElement => {
  const {
    defaultHighlightedIndex,
    disableSelectOnTab,
    downshiftStateReducer,
    filterOnLabel = false,
    initialIsOpen,
    initialSelectedItem,
    inputProps,
    isOpen: isOpenProp,
    readOnly,
    items,
    itemToString = getStringFromLabelFunc,
    label,
    loading,
    onScrollToBottom,
    showLoadingMessageOnFirstLoading = true,
    name,
    onChange,
    onInputValueChange = noop,
    openMenuOnFocus = false,
    DEPRECATEDrenderItem: deprecatedRenderItem,
    renderItem: newRenderItem,
    restrictItemContainerWidth,
    maxItemSelectLimit,
    selectedItemCount = 0,
    selectedItem,
    showSearchIcon = false,
    renderDropdownInPopper,
    dropup,
    popperZIndex,
    triggerInputChangeOnEmpty = false,
    showAddNewItem,
    showAddNewItemAtTop = false,
    controlledInputValue,
    fsItemElement = FullStoryElements.FIELD_MENU_ITEM,
    fsSearchElement = FullStoryElements.FIELD_INPUT,
    fsParent,
    fsType = 'autocomplete',
    fsName,
    enableRecentSelections = false,
    enableRecentSelectionAnalytics = false,
    getRecentSelectionAnalyticsProperties,
    recentSelectionsKey,
    maxRecentSelections = 5,
    refreshRecentSelections,
    ...restProps
  } = props;

  // Validate recent selections configuration
  throwIfInternalTestOrBelow(
    enableRecentSelections === true && !newRenderItem,
    'AutoComplete: When enableRecentSelections is true, the renderItem prop must be provided. ' +
      'Recent selections cannot be rendered without a renderItem function because item.label ' +
      'functions cannot be stored in IndexedDB.',
  );

  throwIfInternalTestOrBelow(
    enableRecentSelections === true && !recentSelectionsKey,
    'AutoComplete: When enableRecentSelections is true, the recentSelectionsKey prop must be provided.',
  );

  throwIfInternalTestOrBelow(
    enableRecentSelectionAnalytics === true && !recentSelectionsKey,
    'AutoComplete: When enableRecentSelectionAnalytics is true, the recentSelectionsKey prop must be provided.',
  );

  const { gray } = useTheme();
  const [isLoading, setLoading] = useState(false);
  const [loadingTimer, setLoadingTimer] = useState<number | null>(null);
  const [isTyping, setIsTyping] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const itemContainerRef = useRef<HTMLDivElement>(null);
  const itemToStringRef = useRef(itemToString);
  const recentListSelectionRef = useRef(false);
  const tabSelectionRef = useRef(false);
  itemToStringRef.current = itemToString;
  const [inputValueStateRaw, setInputValueState] = useState(
    (props.inputProps?.value as string) ?? '',
  );
  const { id } = useWorkflow();

  // Recent selections state and hook
  const [recentItems, setRecentItems] = useState<Shell<ItemType>[]>([]);
  const [recentSelectionsLoaded, setRecentSelectionsLoaded] = useState(false);
  const { loadRecentSelections, saveRecentSelection } =
    useRecentSelections<ItemType>(
      recentSelectionsKey || '',
      maxRecentSelections,
    );
  const shouldManageRecentSelections =
    enableRecentSelections || enableRecentSelectionAnalytics;

  const inputValueState = controlledInputValue
    ? (props.inputProps?.value as string)
    : inputValueStateRaw;

  const { ref: parentContainerRef, maxWidth: maxItemContainerWidth } =
    useMaxItemContainerWidth(restrictItemContainerWidth);

  const {
    styles: popperStyles,
    attributes: popperAttributes,
    state: popperState,
    update: popperUpdate,
  } = usePopper(inputRef.current, itemContainerRef.current, {
    placement: 'bottom-start',
    strategy: 'fixed',
  });

  const [userCanEdit, permissionScope] = useHasPermissionFromContext();

  // It is useful to only show the loading message that the consumer provides after N milliseconds
  // Otherwise you get unhelpful loading jank, especially if the requests run quick (< 500ms)
  useEffect(() => {
    clearTimeout(loadingTimer || 0);
    // if it is the first time that we see a loading prop, set immediately, as we probably don't have any results yet.
    if (loading && loadingTimer === null && showLoadingMessageOnFirstLoading) {
      setLoading(true);
    }
    // if the loadingTimer is null, that means we haven't seen a loading=true prop yet. So the component may have just mounted with no user input.
    else if (loadingTimer === null) {
      return;
    }

    if (loading) {
      const timer = win.setTimeout(() => setLoading(true), 1500);
      setLoadingTimer(timer);
    } else {
      setLoading(false);
      setLoadingTimer(0);
    }
    return (): void => clearTimeout(loadingTimer || 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  // Load recent selections on mount
  useEffect(() => {
    let isCurrent = true;

    if (shouldManageRecentSelections && recentSelectionsKey) {
      setRecentSelectionsLoaded(false);

      const loadRecents = async (): Promise<void> => {
        const stored = (await loadRecentSelections()) as Array<
          Shell<ItemType> & { timestamp: number }
        >;

        if (refreshRecentSelections && stored.length > 0) {
          // Use refresh callback if provided, passing actual timestamps
          const storedData = stored.map((item) => ({
            id: item.id ?? getStringFromItem(item, itemToStringRef.current),
            label: getStringFromItem(item, itemToStringRef.current),
            value: item.value,
            timestamp: item.timestamp,
          }));

          const refreshed = await refreshRecentSelections(storedData);
          const validItems = refreshed
            .map((item, index) => {
              if (item) {
                return item;
              } else if (item === null) {
                // Explicitly deleted, exclude
                return null;
              } else {
                // Undefined, use stored fallback
                return stored[index];
              }
            })
            .filter((item): item is Shell<ItemType> => item !== null);

          if (isCurrent) {
            setRecentItems(validItems);
          }
        } else {
          if (isCurrent) {
            setRecentItems(stored);
          }
        }
        if (isCurrent) {
          setRecentSelectionsLoaded(true);
        }
      };

      void loadRecents();
    }

    return (): void => {
      isCurrent = false;
    };
  }, [
    shouldManageRecentSelections,
    recentSelectionsKey,
    loadRecentSelections,
    refreshRecentSelections,
  ]);

  const debouncedSetIsTyping = useDebouncedFn(setIsTyping, 100, []);

  const isReadOnly = !userCanEdit || readOnly;

  if (isReadOnly) {
    return (
      <>
        {label && <Label>{label}</Label>}
        <ReadOnlyField data-scope={permissionScope} name={name}>
          {(isFunction(selectedItem?.label) &&
            selectedItem?.label(selectedItem?.value)) ||
            (isString(selectedItem?.label) && selectedItem?.label) ||
            (isFunction(initialSelectedItem?.label) &&
              initialSelectedItem?.label(initialSelectedItem?.value)) ||
            (isString(initialSelectedItem?.label) &&
              (initialSelectedItem?.label as string)) ||
            inputProps?.value}
        </ReadOnlyField>
      </>
    );
  }

  // Determine if we should show recent items
  const shouldShowRecentItems =
    enableRecentSelections &&
    items.length === 0 &&
    !inputValueState &&
    recentItems.length > 0;

  const allItemsToRender: Writable<Shell<ItemType>>[] = shouldShowRecentItems
    ? // Show recent items, filtered to exclude disabled ones
      recentItems.filter((item) => !item.disabled)
    : filterOnLabel
      ? items.filter((item) => {
          return (
            item.value === SELECTALL_KEY ||
            checkFilterMatch(
              item,
              trim(inputValueState || '') || '',
              itemToString,
            )
          );
        })
      : [...items];

  // Wrap onChange to save recent selections
  const handleChange = (
    item: Shell<ItemType> | null,
    helpers?: ControllerStateAndHelpers<Shell<ItemType>>,
  ): void => {
    const selectedFromRecentList = recentListSelectionRef.current;
    recentListSelectionRef.current = false;
    if (shouldManageRecentSelections && item && recentSelectionsKey) {
      const itemKey = item.id ?? getStringFromItem(item, itemToString);
      const recentSelectionIndex = recentItems.findIndex(
        (recentItem) =>
          (recentItem.id ?? getStringFromItem(recentItem, itemToString)) ===
          itemKey,
      );

      if (enableRecentSelectionAnalytics) {
        fsEvent(FullstoryEventTypes.AUTOCOMPLETE_RECENT_SELECTION, {
          ...getRecentSelectionAnalyticsProperties?.(item),
          fsName,
          fsParent,
          fsType,
          matchedRecentSelection: recentSelectionIndex >= 0,
          recentSelectionRank:
            recentSelectionIndex >= 0 ? recentSelectionIndex + 1 : undefined,
          recentSelectionCount: recentItems.length,
          recentSelectionsLoaded,
          recentSelectionsUiEnabled: enableRecentSelections,
          selectedFromRecentList:
            selectedFromRecentList && recentSelectionIndex >= 0,
        });
      }

      // Save to recent selections (async, fire and forget)
      void saveRecentSelection(item);

      // Also update the local state so recent items are available immediately
      setRecentItems((prevItems) => {
        const filtered = prevItems.filter(
          (i) => (i.id ?? getStringFromItem(i, itemToString)) !== itemKey,
        );
        return [item, ...filtered].slice(0, maxRecentSelections);
      });
    }
    onChange(item, helpers);
  };

  return (
    <Downshift
      onChange={handleChange}
      itemToString={itemToString}
      defaultHighlightedIndex={defaultHighlightedIndex ?? 0}
      initialSelectedItem={initialSelectedItem}
      initialIsOpen={initialIsOpen}
      isOpen={isOpenProp}
      scrollIntoView={(node): void => {
        node?.scrollIntoView({ block: 'end' });
      }}
      selectedItemChanged={(prev, next): boolean => {
        if (prev?.id) {
          return prev.id !== next?.id;
        } else if (prev?.label) {
          return itemToString(prev) !== itemToString(next);
        }
        // this is the default per Downshift, but we might want to change this to _.isEqual ?
        return prev !== next;
      }}
      onInputValueChange={(value, helpers): void => {
        if (
          'type' in helpers &&
          (helpers.type === Downshift.stateChangeTypes.clickItem ||
            helpers.type === Downshift.stateChangeTypes.keyDownEnter)
        ) {
          recentListSelectionRef.current = shouldShowRecentItems;
        } else {
          recentListSelectionRef.current =
            tabSelectionRef.current &&
            shouldShowRecentItems &&
            (!('type' in helpers) ||
              helpers.type === Downshift.stateChangeTypes.unknown);
        }
        if (!value && helpers.selectedItem?.label) {
          helpers.clearSelection();
        }
        if (
          (helpers.selectedItem?.label || '') !== value ||
          (triggerInputChangeOnEmpty && value === '')
        ) {
          onInputValueChange(value, helpers);
        }
        setIsTyping(true);
        debouncedSetIsTyping(false);
        setInputValueState(value);
      }}
      selectedItem={selectedItem}
      stateReducer={(
        state,
        rawChanges,
      ): Partial<StateChangeOptions<Shell<ItemType>>> => {
        let changes = rawChanges;
        if (
          changes.type ===
          Downshift.stateChangeTypes.controlledPropUpdatedSelectedItem
        ) {
          changes = {
            ...changes,
            inputValue: itemToString(selectedItem || null),
          };
        }
        const allItemsDisabled = allItemsToRender.every(
          (item) => item.disabled,
        );

        // fix for improper highlightedIndex indexing, especially when some items are disabled.
        if (changes.type === Downshift.stateChangeTypes.keyDownArrowDown) {
          const foundItem = allItemsToRender[changes.highlightedIndex ?? -1];
          if (foundItem && foundItem.disabled && !allItemsDisabled) {
            // find the next non-disabled item
            let nextIndex = changes.highlightedIndex ?? -1;
            while (allItemsToRender[nextIndex]?.disabled) {
              // if we are at the end of the list, go to the beginning
              if (nextIndex === allItemsToRender.length - 1) {
                nextIndex = 0;
              } else {
                nextIndex++;
              }
            }
            if (nextIndex !== changes.highlightedIndex) {
              changes.highlightedIndex = nextIndex;
            }
          }
        }

        // fix for improper highlightedIndex indexing, especially when some items are disabled.
        if (changes.type === Downshift.stateChangeTypes.keyDownArrowUp) {
          const foundItem = allItemsToRender[changes.highlightedIndex ?? -1];
          if (foundItem && foundItem.disabled && !allItemsDisabled) {
            // find the next non-disabled item
            let nextIndex = changes.highlightedIndex ?? -1;
            while (allItemsToRender[nextIndex]?.disabled) {
              // if we are at the beginning of the list, go to the end
              if (nextIndex === 0) {
                nextIndex = allItemsToRender.length - 1;
              } else {
                nextIndex--;
              }
            }
            if (nextIndex !== changes.highlightedIndex) {
              changes.highlightedIndex = nextIndex;
            }
          }
        }

        if (downshiftStateReducer) {
          return downshiftStateReducer(state, changes);
        }
        return changes;
      }}
    >
      {(helpers): ReactNode => {
        const {
          clearSelection,
          getInputProps,
          getItemProps,
          getLabelProps,
          getMenuProps,
          getRootProps,
          highlightedIndex,
          inputValue,
          isOpen,
          selectHighlightedItem,
          openMenu,
        } = helpers;
        const generatedItem = showAddNewItem?.({
          listItems: allItemsToRender,
          inputValue,
        });

        if (
          generatedItem &&
          !allItemsToRender.find((i) => (i as anyOk)?.addNew)
        ) {
          generatedItem.addNew = true;
          generatedItem.newLabel = `${
            generatedItem?.prefix ?? t`Create`
          } ${inputValue}`;
          generatedItem.label = inputValue ?? '';

          if (showAddNewItemAtTop) {
            allItemsToRender?.unshift?.(generatedItem as Shell<ItemType>);
          } else {
            allItemsToRender?.push?.(generatedItem as Shell<ItemType>);
          }
        }

        const itemContainer = (
          <ItemContainer
            {...getMenuProps({}, { suppressRefError: true })}
            data-list-wrapper
            data-parentfield={name || inputProps?.name}
            css={{
              bottom: dropup ? `${INPUT_HEIGHT}px` : undefined,
              top: dropup ? 'unset' : undefined,
              // White icons on rollover.
              '[aria-selected=true]:not([data-disabled=true]) svg': {
                color: `${gray.white} !important`,
              },
            }}
            maxWidth={maxItemContainerWidth}
            minWidth={
              (renderDropdownInPopper &&
                popperState?.rects?.reference?.width) ||
              '100%'
            }
            disableClickBubble
            onScrollToBottom={onScrollToBottom}
          >
            {isLoading && (
              <li css={itemStyles} data-testid="autocomplete-loading-msg">
                {isString(loading) ? loading : t`Loading...`}
              </li>
            )}
            {shouldShowRecentItems && (
              <li
                css={{
                  padding: '4px 8px',
                  fontSize: '12px',
                  fontWeight: 600,
                  color: gray[60],
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  pointerEvents: 'none',
                  userSelect: 'none',
                }}
                data-testid="autocomplete-recent-label"
              >
                {t`Recent`}
              </li>
            )}
            {allItemsToRender.map((item, index) => {
              const isHighlighted =
                !item.disabled && highlightedIndex === index;
              const key = String(item.id || itemToString(item));
              const selectedKey = selectedItem
                ? String(selectedItem.id || itemToString(selectedItem))
                : null;

              // Use new renderItem prop if provided. Takes precedence over DEPRECATEDrenderItem.
              if (newRenderItem) {
                const context: RenderItemContext<ItemType> = {
                  item,
                  value: item.value,
                  isHighlighted,
                  disabled: item.disabled,
                  index,
                  isSelected: selectedKey !== null && key === selectedKey,
                };
                return (
                  <Item
                    key={key}
                    isHighlighted={isHighlighted}
                    titleAttr={item?.titleAttr ?? itemToString(item)}
                    disabled={item.disabled}
                    fsElement={fsItemElement}
                    fsParent={fsParent ?? id}
                    fsName={fsName}
                    fsType={fsType}
                    {...omit(
                      getItemProps({
                        index,
                        item,
                        disabled: item.disabled,
                      }),
                      ['key', 'disabled'],
                    )}
                  >
                    {newRenderItem(context)}
                  </Item>
                );
              }

              // Fall back to deprecated renderItem if provided
              if (deprecatedRenderItem) {
                return deprecatedRenderItem({
                  ...helpers,
                  itemProps: {
                    ...getItemProps({ item: omit(item, ['disabled']) }),
                    key,
                  },
                  defaultItemStyles: itemStyles,
                  key,
                  item,
                  getItemProps,
                  index,
                  isHighlighted,
                  disabled: item.disabled,
                  hideCheckbox: item.hideCheckbox,
                  fsItemElement,
                  fsParent: fsParent ?? id,
                  fsType,
                  fsName,
                });
              }

              // Default rendering
              return (
                <Item
                  className={FS_MASK}
                  key={item.id || itemToString(item)}
                  isHighlighted={isHighlighted}
                  titleAttr={
                    item?.titleAttr ? item?.titleAttr : itemToString(item)
                  }
                  disabled={item.disabled}
                  fsElement={fsItemElement}
                  fsParent={fsParent ?? id}
                  fsName={fsName}
                  fsType={fsType}
                  {...omit(
                    getItemProps({
                      index,
                      item: omit(item, ['disabled']),
                      disabled: item.disabled,
                    }),
                    ['key', 'disabled'],
                  )}
                >
                  {item.newLabel}
                  {!item.newLabel &&
                    (isFunction(item.label)
                      ? item.label(item.value)
                      : item.label === ''
                        ? '---'
                        : item.label)}
                </Item>
              );
            })}
          </ItemContainer>
        );

        return (
          <div
            css={{ position: 'relative' }}
            data-scope={permissionScope}
            // This attr is really just for Cypress tests.
            // It helps us know that the dropdown items probably won't change (re-render)
            data-has-settled={!isTyping && !isLoading && !loadingTimer}
            data-fieldname={name || inputProps?.name}
            {...restProps}
            ref={parentContainerRef as fixMe}
          >
            {label && <Label {...getLabelProps()}>{label}</Label>}
            <div
              css={{ position: 'relative', height: '100%' }}
              {...getRootProps({ refKey: 'ref' }, { suppressRefError: true })}
            >
              {showSearchIcon && (
                <Icon
                  i="search"
                  color={gray[70]}
                  size="sm"
                  css={{
                    position: 'absolute',
                    top: 11,
                    left: 9,
                    pointerEvents: 'none',
                  }}
                />
              )}
              <Input
                className={FS_MASK}
                {...(getInputProps({
                  onKeyDown: (e: KeyboardEvent): void => {
                    if (e.key === 'Tab') {
                      if (!disableSelectOnTab && isOpen) {
                        tabSelectionRef.current = true;
                        selectHighlightedItem();
                        tabSelectionRef.current = false;
                      } else if (!inputValue) {
                        // workaround for an upstream downshift issue (technically not a bug)
                        // https://github.com/downshift-js/downshift/issues/714
                        // switching to useCombobox should solve this when its released
                        clearSelection();
                      }
                    }
                  },
                }) as fixMe)}
                ref={inputRef as fixMe}
                title={
                  itemToString(selectedItem || null) || inputValue || undefined
                }
                autoComplete={AUTOCOMPLETE_OFF_VALUE}
                disabled={restProps.disabled}
                css={
                  {
                    background: inputProps?.readOnly ? gray[95] : '',
                    paddingRight: maxItemSelectLimit ? 30 : 8,
                    ...(showSearchIcon && { paddingLeft: 23 }),
                    ...(inputProps?.css as CSSObject),
                  } as CSSObject
                }
                {...omit(inputProps, ['css'])}
                name={inputProps?.name || name}
                onFocus={async (e): Promise<void> => {
                  if (renderDropdownInPopper) {
                    await popperUpdate?.();
                  }
                  inputProps?.onFocus?.(e);
                  if (openMenuOnFocus) {
                    openMenu();
                  }
                }}
                fsParent={fsParent ?? id}
                fsElement={fsSearchElement}
                fsType={fsType}
                fsName={fsName}
              />
              {maxItemSelectLimit && (
                <Tooltip label={t`Select up to ${maxItemSelectLimit} items`}>
                  <div
                    data-testid="max-selection-limit"
                    css={{
                      display: 'block',
                      position: 'absolute',
                      userSelect: 'none',
                      top: 9,
                      right: 9,
                      color: gray[70],
                    }}
                  >{`${selectedItemCount}/${maxItemSelectLimit}`}</div>
                </Tooltip>
              )}
            </div>
            {renderDropdownInPopper ? (
              <>
                {createPortal(
                  <div
                    ref={itemContainerRef}
                    style={{
                      ...popperStyles.popper,
                      zIndex: popperZIndex ?? AUTOCOMPLETE,
                    }}
                    {...popperAttributes.popper}
                  >
                    {isOpen && itemContainer}
                  </div>,
                  getPopperContainer(),
                )}
              </>
            ) : (
              isOpen && itemContainer
            )}
          </div>
        );
      }}
    </Downshift>
  );
};
